import React, { useMemo, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Button,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Grid,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Router as RouterIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  SignalCellularOff as OfflineIcon,
  Refresh as RefreshIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  DeviceHub,
  SignalCellularAlt,
  ArrowForward,
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { format } from 'date-fns';
import { id as localeId } from 'date-fns/locale';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import OLTService, { OLTWithPonData } from '../services/oltService';
import PONDataView from '../components/PONDataView';
import { useAuth } from '../hooks/useAuth';

interface StatCardProps {
  title: string;
  value: string;
  subtitle?: string;
  icon: React.ReactNode;
  color: string;
  loading?: boolean;
  progress?: number;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, subtitle, icon, color, loading, progress }) => (
  <Card
    variant="outlined"
    sx={{
      height: '100%',
      borderLeft: `4px solid ${color}`,
      bgcolor: alpha(color, 0.04),
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {title}
          </Typography>
          {loading ? (
            <CircularProgress size={28} sx={{ my: 1 }} />
          ) : (
            <Typography variant="h5" fontWeight={700} sx={{ color }}>
              {value}
            </Typography>
          )}
          {subtitle && (
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {subtitle}
            </Typography>
          )}
          {progress !== undefined && !loading && (
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{ mt: 1.5, height: 6, borderRadius: 3 }}
              color={progress >= 90 ? 'success' : progress >= 70 ? 'warning' : 'error'}
            />
          )}
        </Box>
        <Box sx={{ color, opacity: 0.9 }}>{icon}</Box>
      </Stack>
    </CardContent>
  </Card>
);

const PORT_COLORS: Record<string, string> = {
  Sehat: '#2e7d32',
  Peringatan: '#ed6c02',
  Kritis: '#d32f2f',
  Nonaktif: '#9e9e9e',
};

const OLTDashboard: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const spopFilter = user?.role === 'operator' && user?.spop_id ? user.spop_id : undefined;

  const [search, setSearch] = useState('');
  const [connectionFilter, setConnectionFilter] = useState('');
  const [includeOffline, setIncludeOffline] = useState(true);
  const [selectedOlt, setSelectedOlt] = useState<string | null>(null);
  const [ponDetailDialog, setPonDetailDialog] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const {
    data: oltResponse,
    isLoading,
    isFetching,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ['oltDashboard', spopFilter, includeOffline],
    queryFn: () =>
      OLTService.getAllOLTsWithPonData({
        spop_id: spopFilter,
        include_offline: includeOffline,
      }),
    refetchInterval: 5 * 60 * 1000,
  });

  const oltData: OLTWithPonData[] = oltResponse?.data || [];
  const lastUpdated = oltResponse?.retrievedAt;

  const statistics = useMemo(() => {
    const stats = {
      totalOLTs: oltData.length,
      connectedOLTs: 0,
      disconnectedOLTs: 0,
      totalPorts: 0,
      totalONUs: 0,
      totalOnline: 0,
      totalOffline: 0,
      healthyPorts: 0,
      warningPorts: 0,
      criticalPorts: 0,
      inactivePorts: 0,
    };

    oltData.forEach((olt) => {
      if (olt.connectionStatus === 'connected') {
        stats.connectedOLTs += 1;
      } else {
        stats.disconnectedOLTs += 1;
      }
      if (olt.statistics) {
        stats.totalPorts += olt.statistics.totalPorts;
        stats.totalONUs += olt.statistics.totalOnus;
        stats.totalOnline += olt.statistics.totalOnline;
        stats.totalOffline += olt.statistics.totalOffline;
        stats.healthyPorts += olt.statistics.healthyPorts;
        stats.warningPorts += olt.statistics.warningPorts;
        stats.criticalPorts += olt.statistics.criticalPorts;
        stats.inactivePorts += olt.statistics.inactivePorts;
      }
    });

    return stats;
  }, [oltData]);

  const portChartData = [
    { name: 'Sehat', value: statistics.healthyPorts },
    { name: 'Peringatan', value: statistics.warningPorts },
    { name: 'Kritis', value: statistics.criticalPorts },
    { name: 'Nonaktif', value: statistics.inactivePorts },
  ].filter((d) => d.value > 0);

  const onlineRate =
    statistics.totalONUs > 0
      ? Math.round((statistics.totalOnline / statistics.totalONUs) * 100)
      : 0;

  const connectionRate =
    statistics.totalOLTs > 0
      ? Math.round((statistics.connectedOLTs / statistics.totalOLTs) * 100)
      : 0;

  const filteredOlts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return oltData.filter((olt) => {
      if (connectionFilter && olt.connectionStatus !== connectionFilter) return false;
      if (!q) return true;
      const haystack = [
        olt.olt.type,
        olt.olt.ip_address,
        olt.olt.spop?.name,
        olt.olt.spop?.areaCode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [oltData, search, connectionFilter]);

  const getHealthIcon = (olt: OLTWithPonData) => {
    if (olt.connectionStatus === 'disconnected') return <OfflineIcon color="error" />;
    const s = olt.statistics;
    if (!s) return <WarningIcon color="warning" />;
    if (s.criticalPorts > 0) return <ErrorIcon color="error" />;
    if (s.warningPorts > 0) return <WarningIcon color="warning" />;
    return <CheckIcon color="success" />;
  };

  const getOnlineRate = (olt: OLTWithPonData) => {
    if (!olt.statistics || olt.statistics.totalOnus === 0) return 0;
    return Math.round((olt.statistics.totalOnline / olt.statistics.totalOnus) * 100);
  };

  const handleViewDetail = (oltId: string) => {
    setSelectedOlt(oltId);
    setDetailError(null);
    setPonDetailDialog(true);
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['oltDashboard'] });
    refetch();
  };

  const errorMessage = queryError
    ? queryError instanceof Error
      ? queryError.message
      : 'Gagal memuat data OLT'
    : null;

  if (isLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={400} gap={2}>
          <CircularProgress />
          <Typography>Memuat dashboard OLT...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: { xs: 2, md: 3 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ sm: 'center' }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            OLT Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Monitoring PON port & status ONU
            {user?.role === 'operator' && user?.spop
              ? ` • SPOP ${user.spop.name}`
              : ' • Semua SPOP'}
            {lastUpdated && (
              <> • Diperbarui {format(new Date(lastUpdated), 'dd/MM/yy HH:mm', { locale: localeId })}</>
            )}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={isFetching ? <CircularProgress size={16} /> : <RefreshIcon />}
            onClick={handleRefresh}
            disabled={isFetching}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<SettingsIcon />}
            onClick={() => navigate('/olts')}
            size="small"
          >
            Kelola OLT
          </Button>
        </Stack>
      </Stack>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {/* KPI */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="OLT Terhubung"
            value={`${statistics.connectedOLTs}/${statistics.totalOLTs}`}
            subtitle={`${statistics.disconnectedOLTs} terputus`}
            icon={<RouterIcon fontSize="large" />}
            color={theme.palette.success.main}
            progress={connectionRate}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Total Port PON"
            value={String(statistics.totalPorts)}
            subtitle={`${statistics.healthyPorts} sehat`}
            icon={<DeviceHub fontSize="large" />}
            color={theme.palette.primary.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Total ONU"
            value={String(statistics.totalONUs)}
            subtitle={`${statistics.totalOnline} online • ${statistics.totalOffline} offline`}
            icon={<SignalCellularAlt fontSize="large" />}
            color={theme.palette.info.main}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatCard
            title="Tingkat Online ONU"
            value={`${onlineRate}%`}
            subtitle={
              statistics.criticalPorts > 0
                ? `${statistics.criticalPorts} port kritis`
                : 'Jaringan stabil'
            }
            icon={<CheckIcon fontSize="large" />}
            color={
              onlineRate >= 90
                ? theme.palette.success.main
                : onlineRate >= 70
                  ? theme.palette.warning.main
                  : theme.palette.error.main
            }
            progress={onlineRate}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Port health chart */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Kesehatan Port PON
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                Agregat semua OLT yang terhubung
              </Typography>
              {portChartData.length === 0 ? (
                <Alert severity="info">Belum ada data port PON</Alert>
              ) : (
                <Box height={220}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={portChartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} allowDecimals={false} />
                      <YAxis type="category" dataKey="name" width={80} tick={{ fontSize: 12 }} />
                      <RechartsTooltip formatter={(v: number) => [`${v} port`, 'Jumlah']} />
                      <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                        {portChartData.map((entry) => (
                          <Cell key={entry.name} fill={PORT_COLORS[entry.name] || '#888'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
              <Stack direction="row" spacing={2} flexWrap="wrap" mt={2}>
                {[
                  { label: 'Sehat', value: statistics.healthyPorts, color: 'success' as const },
                  { label: 'Peringatan', value: statistics.warningPorts, color: 'warning' as const },
                  { label: 'Kritis', value: statistics.criticalPorts, color: 'error' as const },
                  { label: 'Nonaktif', value: statistics.inactivePorts, color: 'default' as const },
                ].map((item) => (
                  <Chip
                    key={item.label}
                    label={`${item.label}: ${item.value}`}
                    color={item.color}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Filter + quick info */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Card variant="outlined" sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Filter & Ringkasan
              </Typography>
              <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Cari OLT / IP / SPOP"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status Koneksi</InputLabel>
                    <Select
                      value={connectionFilter}
                      label="Status Koneksi"
                      onChange={(e) => setConnectionFilter(e.target.value)}
                    >
                      <MenuItem value="">Semua</MenuItem>
                      <MenuItem value="connected">Terhubung</MenuItem>
                      <MenuItem value="disconnected">Terputus</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
              <FormControlLabel
                control={
                  <Switch
                    checked={includeOffline}
                    onChange={(e) => setIncludeOffline(e.target.checked)}
                    size="small"
                  />
                }
                label="Sertakan OLT nonaktif / terputus"
              />

              <Stack spacing={1.5} mt={2}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    OLT dengan masalah koneksi
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main">
                    {statistics.disconnectedOLTs}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Port perlu perhatian (peringatan + kritis)
                  </Typography>
                  <Typography variant="body2" fontWeight={600} color="warning.main">
                    {statistics.warningPorts + statistics.criticalPorts}
                  </Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    ONU offline
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {statistics.totalOffline}
                  </Typography>
                </Stack>
                <Button
                  variant="text"
                  endIcon={<ArrowForward />}
                  onClick={() => navigate('/olts')}
                  sx={{ alignSelf: 'flex-start', mt: 1 }}
                >
                  Buka halaman kelola OLT
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* OLT table */}
      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box px={2} py={2}>
            <Typography variant="h6" fontWeight={600}>
              Daftar OLT ({filteredOlts.length})
            </Typography>
          </Box>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>OLT</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>SPOP</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Koneksi
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Port
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    ONU
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Online
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Port Health
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>
                    Aksi
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredOlts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography color="text.secondary">
                        {oltData.length === 0
                          ? 'Belum ada OLT terdaftar. Tambahkan di halaman Kelola OLT.'
                          : 'Tidak ada OLT yang cocok dengan filter'}
                      </Typography>
                      {oltData.length === 0 && (
                        <Button
                          variant="outlined"
                          sx={{ mt: 2 }}
                          onClick={() => navigate('/olts')}
                        >
                          Tambah OLT
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredOlts.map((olt) => {
                    const rate = getOnlineRate(olt);
                    return (
                      <TableRow key={olt.olt.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            {getHealthIcon(olt)}
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {olt.olt.type}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                                {olt.olt.ip_address}:{olt.olt.port}
                              </Typography>
                              {olt.error && (
                                <Tooltip title={olt.error}>
                                  <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 180, display: 'block' }}>
                                    {olt.error}
                                  </Typography>
                                </Tooltip>
                              )}
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{olt.olt.spop?.name || '-'}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {olt.olt.spop?.areaCode || ''}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={olt.connectionStatus === 'connected' ? 'Terhubung' : 'Terputus'}
                            color={olt.connectionStatus === 'connected' ? 'success' : 'error'}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">{olt.statistics?.totalPorts ?? '-'}</TableCell>
                        <TableCell align="center">
                          {olt.statistics ? (
                            <Typography variant="body2">
                              {olt.statistics.totalOnline}/{olt.statistics.totalOnus}
                            </Typography>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {olt.statistics && olt.statistics.totalOnus > 0 ? (
                            <Box>
                              <Typography variant="body2" fontWeight={600}>
                                {rate}%
                              </Typography>
                              <LinearProgress
                                variant="determinate"
                                value={rate}
                                color={rate >= 90 ? 'success' : rate >= 70 ? 'warning' : 'error'}
                                sx={{ width: 56, mt: 0.5, mx: 'auto' }}
                              />
                            </Box>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {olt.statistics ? (
                            <Stack direction="row" spacing={0.5} justifyContent="center">
                              <Chip label={olt.statistics.healthyPorts} color="success" size="small" />
                              <Chip label={olt.statistics.warningPorts} color="warning" size="small" />
                              <Chip label={olt.statistics.criticalPorts} color="error" size="small" />
                            </Stack>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Detail PON">
                            <span>
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetail(olt.olt.id)}
                                disabled={olt.connectionStatus === 'disconnected'}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={ponDetailDialog} onClose={() => setPonDetailDialog(false)} maxWidth="lg" fullWidth>
        <DialogTitle>Detail Port PON</DialogTitle>
        <DialogContent dividers>
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}
          {selectedOlt && (
            <PONDataView oltId={selectedOlt} onError={(msg: string) => setDetailError(msg)} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPonDetailDialog(false)}>Tutup</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default OLTDashboard;
